import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { VehiclesService } from 'src/app/shared/api-services/vehicles.service';
import { CommunicationService } from 'src/app/shared/utilities/comunication.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { VehicleDisableSchedule } from 'src/app/shared/models/vehicle-disable-schedule/vehicle-disable-schedule';
import { VehicleDisableScheduleRequest } from 'src/app/shared/models/vehicle-disable-schedule/vehicle-disable-schedule-request';
import { TimeSlot } from 'src/app/shared/models/vehicle-disable-schedule/time-slot';
import { AutocompleteModule } from '../../../inputs/autocomplete-primeng/autocomplete.module';
import { UserGroup } from 'src/app/shared/models/user-group/user-group';

@Component({
    selector: 'vehicle-disable-schedule-modal',
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        AutocompleteModule
    ],
    templateUrl: './vehicle-disable-schedule-modal.component.html',
    styleUrls: ['./vehicle-disable-schedule-modal.component.scss']
})
export class VehicleDisableScheduleModalComponent implements OnInit, OnDestroy {

  @Input() vehicleId: string;
  @Input() scheduleId: string;

  form: FormGroup;
  isLoading = false;
  selectedUserGroups: UserGroup[] = [];

  // Per i giorni della settimana
  weekDays = [
    { id: 0, name: this._translateService.instant('SUNDAY') },
    { id: 1, name: this._translateService.instant('MONDAY') },
    { id: 2, name: this._translateService.instant('TUESDAY') },
    { id: 3, name: this._translateService.instant('WEDNESDAY') },
    { id: 4, name: this._translateService.instant('THURSDAY') },
    { id: 5, name: this._translateService.instant('FRIDAY') },
    { id: 6, name: this._translateService.instant('SATURDAY') }
  ];

  // Per i mesi dell'anno
  months = [
    { id: 1, name: this._translateService.instant('JANUARY') },
    { id: 2, name: this._translateService.instant('FEBRUARY') },
    { id: 3, name: this._translateService.instant('MARCH') },
    { id: 4, name: this._translateService.instant('APRIL') },
    { id: 5, name: this._translateService.instant('MAY') },
    { id: 6, name: this._translateService.instant('JUNE') },
    { id: 7, name: this._translateService.instant('JULY') },
    { id: 8, name: this._translateService.instant('AUGUST') },
    { id: 9, name: this._translateService.instant('SEPTEMBER') },
    { id: 10, name: this._translateService.instant('OCTOBER') },
    { id: 11, name: this._translateService.instant('NOVEMBER') },
    { id: 12, name: this._translateService.instant('DECEMBER') }
  ];

  private _destroy$ = new Subject<void>();

  constructor(
    public bsModalRef: BsModalRef,
    private _vehiclesService: VehiclesService,
    private _communicationService: CommunicationService,
    private _translateService: TranslateService,
    private _fb: FormBuilder
  ) {
    this.form = this._fb.group({
      note: [''],
      activeMonths: this._fb.array([]),
      timeSlots: this._fb.array([]),
      userGroupIds: [[]]
    });
  }

  ngOnInit(): void {
    // Inizializziamo i mesi
    this.months.forEach(month => {
      this.activeMonthsFormArray.push(new FormControl(false));
    });

    // Aggiungiamo un time slot vuoto di default
    this.addTimeSlot();

    if (this.scheduleId) {
      this.getVehicleDisableSchedule();
    }
  }

  get activeMonthsFormArray() {
    return this.form.get('activeMonths') as FormArray;
  }

  get timeSlotsFormArray() {
    return this.form.get('timeSlots') as FormArray;
  }

  onUserGroupSelected(group: UserGroup): void {
    if (!this.selectedUserGroups.some(g => g.id === group.id)) {
      this.selectedUserGroups.push(group);
      this.updateUserGroupIds();
    }
  }

  removeUserGroup(groupId: string): void {
    this.selectedUserGroups = this.selectedUserGroups.filter(group => group.id !== groupId);
    this.updateUserGroupIds();
  }

  private updateUserGroupIds(): void {
    const userGroupIds = this.selectedUserGroups.map(group => group.id);
    this.form.patchValue({ userGroupIds });
  }

  private getVehicleDisableSchedule() {
    this.isLoading = true;
    this._vehiclesService.getVehicleDisableSchedule$(this.vehicleId, this.scheduleId, {
      includeUserGroups: true
    })
    .pipe(takeUntil(this._destroy$))
    .subscribe({
      next: (schedule) => {
        this.setValues(schedule);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private setValues(schedule: VehicleDisableSchedule) {
    this.form.patchValue({
      note: schedule.note,
      userGroupIds: schedule.userGroupIds || []
    });

    // Carichiamo i gruppi utente selezionati
    if (schedule.userGroups && schedule.userGroups.length > 0) {
      this.selectedUserGroups = [...schedule.userGroups];
    }

    // Selezioniamo i mesi attivi
    if (schedule.activeMonths && schedule.activeMonths.length > 0) {
      schedule.activeMonths.forEach(monthId => {
        this.activeMonthsFormArray.at(monthId - 1).setValue(true);
      });
    }

    // Svuotiamo l'array delle fasce orarie e inseriamo quelle del record
    this.timeSlotsFormArray.clear();
    if (schedule.timeSlots && schedule.timeSlots.length > 0) {
      schedule.timeSlots.forEach(slot => {
        this.timeSlotsFormArray.push(this.createTimeSlotFormGroup(slot));
      });
    } else {
      this.addTimeSlot();
    }
  }

  createTimeSlotFormGroup(slot?: TimeSlot): FormGroup {
    return this._fb.group({
      dayOfWeek: [slot ? slot.dayOfWeek : 1, Validators.required],
      startTime: [slot ? slot.startTime : 480, [Validators.required, Validators.min(0), Validators.max(1439)]],
      endTime: [slot ? slot.endTime : 1080, [Validators.required, Validators.min(0), Validators.max(1439)]]
    });
  }

  addTimeSlot() {
    this.timeSlotsFormArray.push(this.createTimeSlotFormGroup());
  }

  removeTimeSlot(index: number) {
    this.timeSlotsFormArray.removeAt(index);
  }

  toggleAllMonths(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.activeMonthsFormArray.controls.forEach(control => {
      control.setValue(checked);
    });
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    const selectedMonths = this.activeMonthsFormArray.controls
      .map((control, index) => control.value ? index + 1 : null)
      .filter(monthId => monthId !== null);

    // Prepara i time slots
    const timeSlots = this.form.value.timeSlots.map((slot: { dayOfWeek: number, startTime: number, endTime: number }) => {
      return {
        dayOfWeek: slot.dayOfWeek,
        startTime: slot.startTime,
        endTime: slot.endTime
      };
    });

    const request: VehicleDisableScheduleRequest = {
      note: this.form.value.note,
      activeMonths: selectedMonths,
      timeSlots: timeSlots,
      userGroupIds: this.form.value.userGroupIds
    };

    this.isLoading = true;
    if (this.scheduleId) {
      this._vehiclesService.updateVehicleDisableSchedule$(this.vehicleId, this.scheduleId, request)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => this.onUpdated(),
          error: () => this.isLoading = false
        });
    } else {
      this._vehiclesService.createVehicleDisableSchedule$(this.vehicleId, request)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => this.onUpdated(),
          error: () => this.isLoading = false
        });
    }
  }

  private onUpdated() {
    this.isLoading = false;
    this._communicationService.sendEvent();
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // Converte minuti in formato HH:MM
  minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Converte formato HH:MM in minuti
  timeStringToMinutes(timeString: string): number {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Gestisce il cambio di orario dall'input HTML
  onTimeChange(event: Event, controlName: string, index: number): void {
    const timeString = (event.target as HTMLInputElement).value;
    const minutes = this.timeStringToMinutes(timeString);
    const timeSlotGroup = this.timeSlotsFormArray.at(index) as FormGroup;
    timeSlotGroup.get(controlName)?.setValue(minutes);
  }

  // Ottiene il valore dell'orario in formato stringa per l'input HTML
  getTimeStringValue(index: number, controlName: string): string {
    const timeSlotGroup = this.timeSlotsFormArray.at(index) as FormGroup;
    const minutes = timeSlotGroup.get(controlName)?.value;
    return this.minutesToTimeString(minutes);
  }
} 